import { useState, useRef } from "react";
import { message, Select, Radio, Checkbox } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { endCallApi } from "../../redux/api/Call/EndCallSlice";
import { getAppointment } from "../../redux/action/Appointment";
import { 
  uploadFileApi, 
  consultationObservationApi, 
  clearUploadedFiles 
} from "../../redux/api/Call/ConsultationObservationSlice";
import "./AppointmentScreen.css";
import moment from "moment";
import { useParams } from "react-router-dom";
import { PaperClipOutlined } from '@ant-design/icons';
import { navigateTo } from "../../utils/navigation";
import SharedDocuments from "../../Component/SharedDocuments";

const { Option } = Select;

export default function Index() {
  const dispatch = useDispatch();
  const params = useParams();
  const [memberNotJoined, setMemberNotJoined] = useState(false);
  const [prescriptionNotRequired, setPrescriptionNotRequired] = useState(false);
  const [forceEndWithoutPrescription, setForceEndWithoutPrescription] = useState(false);
  const [riskStatus, setRiskStatus] = useState("");
  const [note, setNote] = useState("");
  const [prescription, setPrescription] = useState("");

  // New observation states
  const [selfHarmSymptoms, setSelfHarmSymptoms] = useState("LOW");
  const [depressionSymptoms, setDepressionSymptoms] = useState("LOW");
  const [anxietySymptoms, setAnxietySymptoms] = useState("LOW");
  const [angerIssues, setAngerIssues] = useState("LOW");

  // New recommendation states
  const [assessments, setAssessments] = useState("");
  const [exercises, setExercises] = useState("");
  const [clinicalConsultation, setClinicalConsultation] = useState(false);
  const [medicalConsultation, setMedicalConsultation] = useState(false);

  // File upload states
  const [assessmentFiles, setAssessmentFiles] = useState([]);
  const [exerciseFiles, setExerciseFiles] = useState([]);
  const [recommendationFiles, setRecommendationFiles] = useState([]);

  // File input refs
  const assessmentFileInputRef = useRef(null);
  const exerciseFileInputRef = useRef(null);
  const recommendationFileInputRef = useRef(null);

  // Redux state
  const { 
    loading: consultationLoading, 
    uploadLoading, 
    uploadedFiles 
  } = useSelector((state) => state.CONSULTATION_OBSERVATION);

  // Loading state for submit
  const [submitLoading, setSubmitLoading] = useState(false);
  const appointment = {
    channel: params.channel,
    mode: params.mode,
    appointmentId: params.id,
    uid: params.uid,
    title: params.title === "-" ? "" : decodeURIComponent(params.title),
    firstName: params.firstName === "-" ? "" : decodeURIComponent(params.firstName),
    lastName: params.lastName === "-" ? "" : decodeURIComponent(params.lastName),
    date: decodeURIComponent(params.date || ""),
    startTime: decodeURIComponent(params.startTime || ""),
  };

  const formattedDateTime = moment(
    `${appointment?.date} ${appointment?.startTime}`,
    "YYYY-MM-DD HH:mm:ss"
  ).format("Do MMMM YYYY h:mm A");

  const riskOptions = [
    { label: "No Risk", value: "NO_RISK", color: "green" },
    { label: "Low", value: "LOW_RISK", color: "goldenrod" },
    { label: "Moderate", value: "MODERATE_RISK", color: "orange" },
    { label: "High", value: "HIGH_RISK", color: "red" },
  ];

  const symptomOptions = [
    { label: "None", value: "NONE" },
    { label: "Low", value: "LOW" },
    { label: "Moderate", value: "MODERATE" },
    { label: "High", value: "HIGH" },
  ];

  // File handling functions
  const handleFileSelect = (type) => {
    // Don't allow file selection when prescription is not required
    if (prescriptionNotRequired) return;
    
    if (type === 'assessment') {
      assessmentFileInputRef.current?.click();
    } else if (type === 'exercise') {
      exerciseFileInputRef.current?.click();
    } else if (type === 'recommendation') {
      recommendationFileInputRef.current?.click();
    }
  };

  const handleFileChange = (e, setFiles) => {
    const files = Array.from(e.target.files);
    setFiles(files);
    e.target.value = '';
  };

  const removeFile = (index, files, setFiles, inputRef = null) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    if (inputRef && inputRef.current) {
      inputRef.current.value = '';
    }
  };

  // File upload helper function
  const uploadFiles = async () => {
    const fileUrls = {};
    const uploadPromises = [];

    // Upload assessment files
    if (assessmentFiles.length > 0) {
      assessmentFiles.forEach((file) => {
        uploadPromises.push(
          dispatch(uploadFileApi(file)).then((result) => {
            if (result.payload.status === "success") {
              fileUrls.assessmentsFileURL = result.payload.url;
              fileUrls.assessmentsFileName = assessments || file.name;
            }
          })
        );
      });
    }

    // Upload exercise files
    if (exerciseFiles.length > 0) {
      exerciseFiles.forEach((file) => {
        uploadPromises.push(
          dispatch(uploadFileApi(file)).then((result) => {
            if (result.payload.status === "success") {
              fileUrls.exercisesFileURL = result.payload.url;
              fileUrls.exercisesFileName = exercises || file.name;
            }
          })
        );
      });
    }

    // Upload recommendation files
    if (recommendationFiles.length > 0) {
      recommendationFiles.forEach((file) => {
        uploadPromises.push(
          dispatch(uploadFileApi(file)).then((result) => {
            if (result.payload.status === "success") {
              fileUrls.recommendationsFileURL = result.payload.url;
              fileUrls.recommendationsFileName = file.name;
            }
          })
        );
      });
    }

    await Promise.all(uploadPromises);
    return fileUrls;
  };

  const validateAndEndCall = () => {
    const payload = {};
    dispatch(getAppointment(payload, 1000)).then((res) => {
      let selectedData = Object.values(res?.payload || {}).flat();
      let filterData = selectedData.filter((item) => item.appointmentId === appointment?.appointmentId);

      if (Array.isArray(filterData) && filterData.length > 0) {
        if (
          memberNotJoined ||
          filterData[0].studentJoined === null ||
          !filterData[0].studentJoined
        ) {
          // Student didn't join - allow ending with incomplete status
          setMemberNotJoined(true);
          handleEndCall();
        } else {
          // Check if recommendations are provided (any of: prescription text/files, assessments text/files,
          // exercises text/files, clinical consultation, or medical consultation)
          const hasRecommendationText = prescription && prescription.trim() !== "";
          const hasRecommendationFiles = recommendationFiles && recommendationFiles.length > 0;
          const hasAssessmentText = assessments && assessments.trim() !== "";
          const hasAssessmentFiles = assessmentFiles && assessmentFiles.length > 0;
          const hasExerciseText = exercises && exercises.trim() !== "";
          const hasExerciseFiles = exerciseFiles && exerciseFiles.length > 0;
          const hasClinicalConsultation = clinicalConsultation === true;
          const hasMedicalConsultation = medicalConsultation === true;

          const hasRecommendationContent = hasRecommendationText || hasRecommendationFiles ||
                                          hasAssessmentText || hasAssessmentFiles ||
                                          hasExerciseText || hasExerciseFiles ||
                                          hasClinicalConsultation || hasMedicalConsultation;

          if (forceEndWithoutPrescription || prescriptionNotRequired || hasRecommendationContent) {
            handleEndCall();
          } else {
            // Show message asking if user wants to continue without prescription
            const shouldContinue = window.confirm(
              "No recommendations provided. Do you want to continue and mark this appointment as incomplete?"
            );
            if (shouldContinue) {
              setForceEndWithoutPrescription(true);
              handleEndCall();
            }
          }
        }
      } else {
        // No appointment data found - proceed with ending
        handleEndCall();
      }
    });
  };

  const handleEndCall = async () => {
    // Validate risk status is provided when needed
    if (!memberNotJoined && !riskStatus) {
      message.error("Please select a risk status before ending the session.");
      return;
    }

    setSubmitLoading(true);

    try {
      // Step 1: Upload files first
      const fileUrls = await uploadFiles();

      // Step 2: Send consultation observation data
      const observationData = {
        appointmentId: appointment?.appointmentId,
        selfHarmSymptoms: selfHarmSymptoms,
        depressionSymptoms: depressionSymptoms,
        anxietySymptoms: anxietySymptoms,
        angerIssues: angerIssues,
        clinicalConsultation: clinicalConsultation,
        medicalConsultation: medicalConsultation,
        notesText: note || "",
        recommendationText: prescription || "",
        noPrescriptionRequired: prescriptionNotRequired,
        ...fileUrls
      };

      const observationResult = await dispatch(consultationObservationApi(observationData));

      // Step 3: Update appointment status using existing logic
      if (observationResult.payload.status === "success") {
        let userData = {
          id: appointment?.appointmentId,
          counsellorJoined: true,
          notes: note?.trim() === "" ? null : note,
        };
        
        if (memberNotJoined) {
          // Member not joined - no risk status needed
          userData = {
            ...userData,
            studentJoined: false,
            status: "STUDENT_MISSED",
          };
        } else if (prescriptionNotRequired) {
          // Prescription not required - risk status is mandatory
          if (!riskStatus) {
            message.error("Risk status is required.");
            setSubmitLoading(false);
            return;
          }
          userData = {
            ...userData,
            prescriptionRequired: false,
            status: "COMPLETED",
            riskStatus: riskStatus,
          };
        } else {
          // Normal flow - risk status is mandatory
          if (!riskStatus) {
            message.error("Risk status is required.");
            setSubmitLoading(false);
            return;
          }

          // Determine completion status based on any recommendation data
          const hasRecommendationText = prescription && prescription.trim() !== "";
          const hasRecommendationFiles = recommendationFiles && recommendationFiles.length > 0;
          const hasAssessmentText = assessments && assessments.trim() !== "";
          const hasAssessmentFiles = assessmentFiles && assessmentFiles.length > 0;
          const hasExerciseText = exercises && exercises.trim() !== "";
          const hasExerciseFiles = exerciseFiles && exerciseFiles.length > 0;
          const hasClinicalConsultation = clinicalConsultation === true;
          const hasMedicalConsultation = medicalConsultation === true;

          const hasRecommendations = hasRecommendationText || hasRecommendationFiles ||
                                     hasAssessmentText || hasAssessmentFiles ||
                                     hasExerciseText || hasExerciseFiles ||
                                     hasClinicalConsultation || hasMedicalConsultation;

          if (forceEndWithoutPrescription && !hasRecommendations) {
            // Force end without recommendations - mark as INCOMPLETE
            userData = {
              ...userData,
              studentJoined: true,
              prescription: prescription?.trim() === "" ? null : prescription,
              riskStatus: riskStatus,
              status: "IN_COMPLETE",
            };
          } else {
            // Normal completion logic
            userData = {
              ...userData,
              studentJoined: true,
              prescription: prescription?.trim() === "" ? null : prescription,
              riskStatus: riskStatus,
              status: hasRecommendations ? "COMPLETED" : "IN_COMPLETE",
            };
          }
        }

        const res = await dispatch(endCallApi(userData));
        if (res.payload?.status === "success") {
          // Clear uploaded files from Redux state
          dispatch(clearUploadedFiles());
          message.success("Appointment Completed").then(() => {
            navigateTo("/upcoming-appointments/upcoming");
          });
        } else {
          message.error("Something went wrong. Please try again.");
        }
      } else {
        message.error("Failed to save consultation data. Please try again.");
      }
    } catch (error) {
      console.error(error);
      message.error("An error occurred while ending the call.");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="appointment-screen">
      <div className="appointment-left">
        {/* Header Section */}
        <div className="new-appointment-header">
          <div className="header-row">
            <div className="appt-id-section">
              <span className="appt-label">Appt ID</span>
              <span className="appt-value">{appointment?.appointmentId}</span>
            </div>
            <div className="date-time-section">
              <span className="datetime-value">{formattedDateTime.split(' ').slice(0, 3).join(' ')}</span>
              <span className="time-value">{formattedDateTime.split(' ').slice(3).join(' ')}</span>
            </div>
          </div>
          <div className="header-row">
            <div className="patient-section">
              <span className="patient-label">Patient</span>
              <div className="patient-info-new">
                <span className="patient-name">
                  {appointment?.title} {appointment?.firstName} {appointment?.lastName}
                </span>
                <div className="patient-dot"></div>
                <span className="patient-age">28 Y</span>
              </div>
            </div>
            <div className="member-status">
              <Checkbox
                checked={memberNotJoined}
                onChange={(e) => {
                  setMemberNotJoined(e.target.checked);
                  if (e.target.checked) {
                    setPrescriptionNotRequired(false);
                  }
                }}
                disabled={prescription?.trim() !== ""}
              />
              <span className="member-status-text">Member not joined</span>
            </div>
          </div>
          <div className="header-separator"></div>
        </div>

        {/* Refreshes every 30s since the client can share during the session */}
        <SharedDocuments appointmentId={appointment.appointmentId} pollInterval={30000} />

        {/* Observations Section */}
        <div className="observations-section">
          <h2 className="section-title">Observations</h2>
          <div className="inperson-separator"></div>

          <div className="symptoms-grid">
            <div className="symptom-field">
              <label className="field-label">Self Harm Symptoms</label>
              <Select
                placeholder="Select"
                value={selfHarmSymptoms}
                onChange={setSelfHarmSymptoms}
                className="custom-select"
              >
                {symptomOptions.map(option => (
                  <Option key={option.value} value={option.value}>{option.label}</Option>
                ))}
              </Select>
            </div>
            <div className="symptom-field">
              <label className="field-label">Depression Symptoms</label>
              <Select
                placeholder="Select"
                value={depressionSymptoms}
                onChange={setDepressionSymptoms}
                className="custom-select"
              >
                {symptomOptions.map(option => (
                  <Option key={option.value} value={option.value}>{option.label}</Option>
                ))}
              </Select>
            </div>
            <div className="symptom-field">
              <label className="field-label">Anxiety Symptoms</label>
              <Select
                placeholder="Select"
                value={anxietySymptoms}
                onChange={setAnxietySymptoms}
                className="custom-select"
              >
                {symptomOptions.map(option => (
                  <Option key={option.value} value={option.value}>{option.label}</Option>
                ))}
              </Select>
            </div>
            <div className="symptom-field">
              <label className="field-label">Anger Issues</label>
              <Select
                placeholder="Select"
                value={angerIssues}
                onChange={setAngerIssues}
                className="custom-select"
              >
                {symptomOptions.map(option => (
                  <Option key={option.value} value={option.value}>{option.label}</Option>
                ))}
              </Select>
            </div>
          </div>

          <div className="inperson-separator"></div>

          <div className="note-field">
            <label className="field-label">Note</label>
            <textarea
              className="custom-textarea"
              placeholder="Type here"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        {/* Recommendations Section */}
        <div className="recommendations-section">
          <div className="recommendations-header">
            <h2 className="section-title">Recommendations</h2>
            <div className="prescription-not-required">
              <Checkbox
                checked={prescriptionNotRequired}
                onChange={(e) => {
                  if (!memberNotJoined) {
                    const isChecked = e.target.checked;
                    setPrescriptionNotRequired(isChecked);

                    // Clear all recommendation data when marking as "not required"
                    if (isChecked) {
                      setAssessmentFiles([]);
                      setExerciseFiles([]);
                      setRecommendationFiles([]);
                      setAssessments("");
                      setExercises("");
                      setPrescription("");
                      setClinicalConsultation(false);
                      setMedicalConsultation(false);

                      // Clear file input refs
                      if (assessmentFileInputRef.current) {
                        assessmentFileInputRef.current.value = '';
                      }
                      if (exerciseFileInputRef.current) {
                        exerciseFileInputRef.current.value = '';
                      }
                      if (recommendationFileInputRef.current) {
                        recommendationFileInputRef.current.value = '';
                      }
                    }
                  }
                }}
                disabled={memberNotJoined}
              />
              <span className="not-required-text">Prescription not required</span>
            </div>
          </div>

          <div className="inperson-separator"></div>

          <div className="recommendation-fields">
            <div className="recommendation-field">
              <label className="field-label">Assessments</label>
              <div className="input-with-attachment">
                <input
                  type="text"
                  className="custom-input"
                  placeholder="Type here..."
                  value={assessments}
                  onChange={(e) => setAssessments(e.target.value)}
                  disabled={prescriptionNotRequired}
                  style={{ cursor: prescriptionNotRequired ? 'not-allowed' : 'text' }}
                />
                <input
                  type="file"
                  ref={assessmentFileInputRef}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(e, setAssessmentFiles)}
                  disabled={prescriptionNotRequired}
                />
                <button
                  className="attachment-button"
                  onClick={() => handleFileSelect('assessment')}
                  type="button"
                  disabled={prescriptionNotRequired}
                >
                  <PaperClipOutlined />
                </button>
              </div>
              {assessmentFiles.length > 0 && (
                <div className="file-list">
                  {assessmentFiles.map((file, index) => (
                    <div key={index} className="file-item">
                      <span className="file-name">{file.name}</span>
                      <button
                        className="file-remove"
                        onClick={() => removeFile(index, assessmentFiles, setAssessmentFiles, assessmentFileInputRef)}
                        type="button"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="recommendation-field">
              <label className="field-label">Exercises</label>
              <div className="input-with-attachment">
                <input
                  type="text"
                  className="custom-input"
                  placeholder="Type here..."
                  value={exercises}
                  onChange={(e) => setExercises(e.target.value)}
                  disabled={prescriptionNotRequired}
                  style={{ cursor: prescriptionNotRequired ? 'not-allowed' : 'text' }}
                />
                <input
                  type="file"
                  ref={exerciseFileInputRef}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(e, setExerciseFiles)}
                  disabled={prescriptionNotRequired}
                />
                <button
                  className="attachment-button"
                  onClick={() => handleFileSelect('exercise')}
                  type="button"
                  disabled={prescriptionNotRequired}
                >
                  <PaperClipOutlined />
                </button>
              </div>
              {exerciseFiles.length > 0 && (
                <div className="file-list">
                  {exerciseFiles.map((file, index) => (
                    <div key={index} className="file-item">
                      <span className="file-name">{file.name}</span>
                      <button
                        className="file-remove"
                        onClick={() => removeFile(index, exerciseFiles, setExerciseFiles, exerciseFileInputRef)}
                        type="button"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="consultation-field">
              <label className="field-label">Clinical Consultation</label>
              <Radio.Group
                value={clinicalConsultation}
                onChange={(e) => setClinicalConsultation(e.target.value)}
                className="radio-group"
                disabled={prescriptionNotRequired}
              >
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </div>

            <div className="consultation-field">
              <label className="field-label">Medical Consultation</label>
              <Radio.Group
                value={medicalConsultation}
                onChange={(e) => setMedicalConsultation(e.target.value)}
                className="radio-group"
                disabled={prescriptionNotRequired}
              >
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </div>
          </div>

          <div className="inperson-separator"></div>

          <div className="prescription-field">
            <label className="field-label">Prescription</label>
            <textarea
              className="custom-textarea"
              placeholder="Type here"
              value={prescription}
              onChange={(e) => {
                setMemberNotJoined(false);
                setPrescription(e.target.value);
              }}
              disabled={prescriptionNotRequired || memberNotJoined}
            />
          </div>
        </div>
      </div>

      <div className="appointment-right">
        {memberNotJoined ? (
          <div className="right-panel">
            <div></div>
            <h3>End Call Confirmation</h3>
            <p>Are you sure you want to end the call?</p>
            <p>You waited for the member for 15 minutes.</p>
            <div className="divider"></div>
            <button 
              onClick={validateAndEndCall} 
              disabled={submitLoading || consultationLoading}
            >
              {submitLoading || consultationLoading ? "Saving..." : "End Session"}
            </button>
          </div>
        ) : prescriptionNotRequired ? (
          <div className="right-panel">
            <div className="risk-status-radio">
            <h3>Rate Risk Status</h3>
            <div className="divider"></div>
            <p>Based on your assessment, select the member’s risk status:</p>
            <div className="risk-options">
              {riskOptions.map(({ label, value, color }) => (
                <label key={value}>
                  <input
                    type="radio"
                    name="riskStatus"
                    value={value}
                    checked={riskStatus === value}
                    onChange={(e) => setRiskStatus(e.target.value)}
                    className="risk-radio"
                    style={{
                      accentColor: riskStatus === value ? color : undefined,
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
            </div>
            <div className="divider-break"></div>
            <h4>Prescription not required</h4>
            <p>Are you sure there is no prescription required?</p>
            <div className="divider"></div>
            <div className="right-panel-actions">
              <button 
                disabled={!riskStatus || submitLoading || consultationLoading} 
                onClick={validateAndEndCall}
              >
                {submitLoading || consultationLoading ? "Saving..." : "End Session"}
              </button>
            </div>
          </div>
        ) : (
          <div className="right-panel">
            <h3>Rate Risk Status</h3>
            <div className="divider"></div>
            <p>Based on your assessment, select the member’s risk status:</p>
            <div className="risk-options">
              {riskOptions.map(({ label, value, color }) => (
                <label key={value}>
                  <input
                    type="radio"
                    name="riskStatus"
                    value={value}
                    checked={riskStatus === value}
                    onChange={(e) => setRiskStatus(e.target.value)}
                    className="risk-radio"
                    style={{
                      accentColor: riskStatus === value ? color : undefined,
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
            <div className="divider-break"></div>
            <h4>Will update prescription later</h4>
            <p>You can update the prescription from appointments history.</p>
            <div className="divider"></div>
            <div className="right-panel-actions">
              <button 
                onClick={validateAndEndCall} 
                disabled={!riskStatus || submitLoading || consultationLoading}
              >
                {submitLoading || consultationLoading ? "Saving..." : "End Session"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
